import { useMemo, memo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

import { ConditionItemProps, ConditionPart } from '../types';
import {
    ConditionText,
    ClickableArgument,
    RegularArgument,
    PromptOverrideBox,
    PromptOverrideText,
} from './ConditionItem.styles';

export const ConditionItem = memo(
    ({
        condition,
        isMet,
        argumentMetadataMap,
        showPromptOverride,
        setShowPromptOverride,
        index,
        arg_metadata_id,
    }: ConditionItemProps) => {
        const hasClickedArgInThisCondition = useMemo(() => {
            return showPromptOverride === index;
        }, [showPromptOverride, index]);

        const conditionParts = useMemo((): ConditionPart[] => {
            const matches = condition.match(/"([^"]+)"/g);
            if (!matches) return [];

            return matches.map((match) => {
                const argName = match.replace(/"/g, '');
                const argMetadata = arg_metadata_id ? argumentMetadataMap.get(arg_metadata_id) : undefined;
                const promptOverride = argMetadata?.params?.prompt_override;

                return {
                    argName,
                    hasPromptOverride: typeof promptOverride === 'string' ? promptOverride : null,
                    isCurrentlyShown: showPromptOverride === index,
                };
            });
        }, [condition, argumentMetadataMap, showPromptOverride, index, arg_metadata_id]);

        const promptOverrideData = useMemo(() => {
            if (!hasClickedArgInThisCondition || showPromptOverride === null) return null;
            const argMetadata = arg_metadata_id ? argumentMetadataMap.get(arg_metadata_id) : undefined;
            const promptOverride = argMetadata?.params?.prompt_override;
            return typeof promptOverride === 'string' ? promptOverride : null;
        }, [hasClickedArgInThisCondition, showPromptOverride, argumentMetadataMap, arg_metadata_id]);

        const trailingValue = useMemo(() => {
            const match = condition.match(/"[^"]+"([^"]*)-/);
            if (!match) return null;
            const afterQuote = match[1].trim();
            return afterQuote.length > 0 ? afterQuote : null;
        }, [condition]);

        const conditionValue = useMemo(() => {
            const match = condition.match(/\(([^)]+)\)$/);
            return match ? match[1] : null;
        }, [condition]);

        return (
            <Box>
                <ConditionText variant="body2" color="text.secondary" $isMet={isMet}>
                    {conditionParts.map((part, idx) => {
                        const { argName, hasPromptOverride, isCurrentlyShown } = part;
                        const ArgumentComponent = hasPromptOverride ? ClickableArgument : RegularArgument;
                        const argumentProps = hasPromptOverride
                            ? {
                                  component: 'span',
                                  onClick: () => setShowPromptOverride(isCurrentlyShown ? null : index),
                              }
                            : { component: 'span' };
                        return (
                            <Tooltip key={idx} title={trailingValue || ''} arrow disableHoverListener={!trailingValue}>
                                <ArgumentComponent {...argumentProps}>&quot;{argName}&quot;</ArgumentComponent>
                            </Tooltip>
                        );
                    })}
                    {conditionValue && (
                        <Box sx={{ ml: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {conditionValue}
                            </Typography>
                        </Box>
                    )}
                </ConditionText>
                {promptOverrideData && (
                    <PromptOverrideBox>
                        <PromptOverrideText variant="body2">{promptOverrideData}</PromptOverrideText>
                    </PromptOverrideBox>
                )}
            </Box>
        );
    },
);

ConditionItem.displayName = 'ConditionItem';
